import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import {
  Settings as SettingsIcon,
  Building2,
  Users,
  Shield,
  Mail,
  Link2,
  Plus,
  RefreshCw,
  Trash2,
  Copy,
  Edit,
  CheckCircle2,
  AlertCircle,
  Phone,
  Send,
  Moon,
  Sun,
  Monitor,
  Palette,
} from "lucide-react";
import { useThemeStore, type ThemeMode } from "../stores/themeStore";
import { Button } from "../components/ui/Button";
import { Card, CardHeader, CardTitle } from "../components/ui/Card";
import { Badge } from "../components/ui/Badge";
import { StatusBadge } from "../components/ui/StatusBadge";
import { Modal } from "../components/ui/Modal";
import { Input } from "../components/ui/Input";
import { LoadingSpinner } from "../components/ui/LoadingSpinner";
import api from "../lib/api";
import { useAppStore } from "../stores/appStore";
import { useAuthStore } from "../stores/authStore";
import { formatDate } from "../lib/utils";

type SettingsTab = "organization" | "users" | "roles" | "invitations" | "sso";

interface OrgDetails {
  id: string;
  name: string;
  slug: string;
  industry?: string | null;
  contact_email: string;
  contact_phone?: string | null;
  contact_person?: string | null;
  address?: string | null;
  status: string;
  created_at: string;
  updated_at: string;
}

interface OrgUser {
  id: string;
  email: string;
  full_name: string;
  is_active: boolean;
  roles: Array<{
    role: string;
    virtual_site_id?: string;
  }>;
}

interface Invitation {
  id: string;
  email: string;
  role: string;
  virtual_site_id?: string;
  accepted: boolean;
  created_at: string;
  expires_at: string;
}

interface SSOConfig {
  id: string;
  provider: string;
  client_id: string;
  enabled: boolean;
  created_at: string;
}

export default function Settings() {
  const { selectedOrgId } = useAppStore();
  const { user, organizations, fetchMe } = useAuthStore();
  const [activeTab, setActiveTab] = useState<SettingsTab>("organization");
  const [users, setUsers] = useState<OrgUser[]>([]);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [ssoConfigs, setSsoConfigs] = useState<SSOConfig[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Full org details fetched separately — OrgRoleInfo (auth store) only has
  // name+slug+roles, but the Organization tab shows industry/contact/address.
  const [orgDetails, setOrgDetails] = useState<OrgDetails | null>(null);
  const [orgLoading, setOrgLoading] = useState(false);

  const [showInvite, setShowInvite] = useState(false);
  const [inviteForm, setInviteForm] = useState({
    email: "",
    role: "user",
  });
  const [isInviting, setIsInviting] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);

  // Edit organization
  const [showEditOrg, setShowEditOrg] = useState(false);
  const [orgForm, setOrgForm] = useState({
    name: "", industry: "", contact_email: "", contact_phone: "",
    contact_person: "", address: "", status: "active",
  });
  const [isSavingOrg, setIsSavingOrg] = useState(false);
  const [orgError, setOrgError] = useState<string | null>(null);

  // Edit profile
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({ full_name: "", phone: "" });
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);

  // Verification (email or phone)
  const [verifyKind, setVerifyKind] = useState<"email" | "phone" | null>(null);
  const [verifyCode, setVerifyCode] = useState("");
  const [verifyError, setVerifyError] = useState<string | null>(null);
  const [verifyInfo, setVerifyInfo] = useState<string | null>(null);
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);

  const currentOrg = (organizations || []).find((o) => o.id === selectedOrgId);

  const themeMode = useThemeStore((s) => s.mode);
  const setThemeMode = useThemeStore((s) => s.setMode);
  const [themeSaving, setThemeSaving] = useState<ThemeMode | null>(null);

  async function handleThemeChange(mode: ThemeMode) {
    if (mode === themeMode) return;
    setThemeSaving(mode);
    try {
      await setThemeMode(mode);
    } finally {
      setThemeSaving(null);
    }
  }

  const fetchOrgDetails = useCallback(async () => {
    if (!selectedOrgId) {
      setOrgDetails(null);
      return;
    }
    setOrgLoading(true);
    try {
      const res = await api.get(`/organizations/${selectedOrgId}`);
      setOrgDetails(res.data);
    } catch {
      setOrgDetails(null);
    } finally {
      setOrgLoading(false);
    }
  }, [selectedOrgId]);

  const fetchUsers = useCallback(async () => {
    if (!selectedOrgId) return;
    setIsLoading(true);
    try {
      const res = await api.get(
        `/organizations/${selectedOrgId}/users`
      );
      setUsers(res.data.items || res.data || []);
    } catch {
      setUsers([]);
    } finally {
      setIsLoading(false);
    }
  }, [selectedOrgId]);

  const fetchInvitations = useCallback(async () => {
    if (!selectedOrgId) return;
    setIsLoading(true);
    try {
      const res = await api.get(
        `/organizations/${selectedOrgId}/invitations`
      );
      setInvitations(res.data.items || res.data || []);
    } catch {
      setInvitations([]);
    } finally {
      setIsLoading(false);
    }
  }, [selectedOrgId]);

  const fetchSSO = useCallback(async () => {
    if (!selectedOrgId) return;
    setIsLoading(true);
    try {
      const res = await api.get(
        `/organizations/${selectedOrgId}/sso-configs`
      );
      setSsoConfigs(res.data.items || res.data || []);
    } catch {
      setSsoConfigs([]);
    } finally {
      setIsLoading(false);
    }
  }, [selectedOrgId]);

  useEffect(() => {
    if (activeTab === "organization") fetchOrgDetails();
    else if (activeTab === "users" || activeTab === "roles") fetchUsers();
    else if (activeTab === "invitations") fetchInvitations();
    else if (activeTab === "sso") fetchSSO();
  }, [activeTab, fetchOrgDetails, fetchUsers, fetchInvitations, fetchSSO]);

  function openEditOrg() {
    if (!orgDetails) return;
    setOrgForm({
      name: orgDetails.name || "",
      industry: orgDetails.industry || "",
      contact_email: orgDetails.contact_email || "",
      contact_phone: orgDetails.contact_phone || "",
      contact_person: orgDetails.contact_person || "",
      address: orgDetails.address || "",
      status: orgDetails.status || "active",
    });
    setOrgError(null);
    setShowEditOrg(true);
  }

  async function handleSaveOrg() {
    if (!selectedOrgId) return;
    if (!orgForm.name.trim()) {
      setOrgError("Name is required");
      return;
    }
    setIsSavingOrg(true);
    setOrgError(null);
    try {
      // Backend's Query(None) ignores None params, so empty strings clear
      // optional text fields. Status is an enum — only send when set.
      const params: Record<string, string> = {
        name: orgForm.name.trim(),
        industry: orgForm.industry,
        contact_email: orgForm.contact_email,
        contact_phone: orgForm.contact_phone,
        contact_person: orgForm.contact_person,
        address: orgForm.address,
      };
      if (orgForm.status) params.status = orgForm.status;
      const res = await api.put(`/organizations/${selectedOrgId}`, null, { params });
      setOrgDetails(res.data);
      setShowEditOrg(false);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string } } };
      setOrgError(error.response?.data?.detail || "Failed to update organization");
    } finally {
      setIsSavingOrg(false);
    }
  }

  function openEditProfile() {
    if (!user) return;
    setProfileForm({
      full_name: user.full_name || "",
      phone: user.phone || "",
    });
    setProfileError(null);
    setShowEditProfile(true);
  }

  async function handleSaveProfile() {
    if (!user) return;
    if (!profileForm.full_name.trim()) {
      setProfileError("Name is required");
      return;
    }
    setIsSavingProfile(true);
    setProfileError(null);
    try {
      const params: Record<string, string> = {
        full_name: profileForm.full_name.trim(),
      };
      // Only send phone when it changed — sending an empty string would clear it.
      if (profileForm.phone !== (user.phone || "")) {
        params.phone = profileForm.phone;
      }
      await api.put(`/users/${user.id}`, null, { params });
      // Refresh the auth store so the displayed profile updates everywhere.
      await fetchMe();
      setShowEditProfile(false);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string } } };
      setProfileError(error.response?.data?.detail || "Failed to update profile");
    } finally {
      setIsSavingProfile(false);
    }
  }

  async function openVerify(kind: "email" | "phone") {
    setVerifyKind(kind);
    setVerifyCode("");
    setVerifyError(null);
    setVerifyInfo(null);
    // Trigger an OTP send right away — saves the user one click.
    await sendOtp(kind);
  }

  async function sendOtp(kind: "email" | "phone") {
    if (!user) return;
    setIsSendingOtp(true);
    setVerifyError(null);
    try {
      await api.post("/auth/resend-otp", null, {
        params: {
          email: user.email,
          channel: kind === "email" ? "email" : "sms",
          purpose: kind === "email" ? "email_verification" : "phone_verification",
        },
      });
      setVerifyInfo(
        kind === "email"
          ? `Code sent to ${user.email}.`
          : `Code sent to ${user.phone || "your phone"} via SMS.`,
      );
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string } } };
      setVerifyError(error.response?.data?.detail || "Could not send code");
    } finally {
      setIsSendingOtp(false);
    }
  }

  async function handleVerify() {
    if (!user || !verifyKind) return;
    if (verifyCode.length !== 6) {
      setVerifyError("Enter the 6-digit code");
      return;
    }
    setIsVerifying(true);
    setVerifyError(null);
    try {
      if (verifyKind === "email") {
        await api.post("/auth/verify-email", null, {
          params: { email: user.email, code: verifyCode },
        });
      } else {
        await api.post("/auth/verify-phone", null, {
          params: { code: verifyCode },
        });
      }
      await fetchMe();
      setVerifyKind(null);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string } } };
      setVerifyError(error.response?.data?.detail || "Verification failed");
    } finally {
      setIsVerifying(false);
    }
  }

  async function handleInvite() {
    if (!selectedOrgId) return;
    setIsInviting(true);
    setInviteError(null);
    try {
      await api.post(
        `/organizations/${selectedOrgId}/invitations`,
        null,
        {
          params: {
            email: inviteForm.email,
            role: inviteForm.role,
          },
        }
      );
      setShowInvite(false);
      setInviteForm({ email: "", role: "user" });
      fetchInvitations();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string } } };
      setInviteError(
        error.response?.data?.detail || "Failed to send invitation"
      );
    } finally {
      setIsInviting(false);
    }
  }

  const tabs: { key: SettingsTab; label: string; icon: React.ReactNode }[] = [
    { key: "organization", label: "Organization", icon: <Building2 className="h-4 w-4" /> },
    { key: "users", label: "Users", icon: <Users className="h-4 w-4" /> },
    { key: "roles", label: "Roles", icon: <Shield className="h-4 w-4" /> },
    { key: "invitations", label: "Invitations", icon: <Mail className="h-4 w-4" /> },
    { key: "sso", label: "SSO", icon: <Link2 className="h-4 w-4" /> },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      <div>
        <h1 className="text-2xl font-bold text-surface-100 flex items-center gap-2">
          <SettingsIcon className="h-6 w-6" />
          Settings
        </h1>
        <p className="text-sm text-surface-400 mt-1">
          Manage your organization, users, and configurations
        </p>
      </div>

      {/* Tab nav */}
      <div className="flex gap-1 border-b border-surface-700/50 overflow-x-auto">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-all border-b-2 -mb-px whitespace-nowrap ${
              activeTab === tab.key
                ? "border-brand-500 text-brand-300"
                : "border-transparent text-surface-400 hover:text-surface-200"
            }`}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* Organization Tab */}
      {activeTab === "organization" && (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between w-full">
                <CardTitle>Organization Details</CardTitle>
                <div className="flex items-center gap-2">
                  <Button variant="secondary" size="icon" onClick={fetchOrgDetails}>
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={openEditOrg}
                    disabled={!orgDetails}
                  >
                    <Edit className="h-4 w-4" />
                    Edit
                  </Button>
                </div>
              </div>
            </CardHeader>
            {orgLoading ? (
              <div className="flex justify-center py-8">
                <LoadingSpinner size="md" />
              </div>
            ) : orgDetails ? (
              <dl className="space-y-4">
                <div className="flex items-center justify-between">
                  <dt className="text-sm text-surface-400">Name</dt>
                  <dd className="text-sm font-medium text-surface-200">
                    {orgDetails.name}
                  </dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt className="text-sm text-surface-400">Slug</dt>
                  <dd className="flex items-center gap-2 text-sm text-surface-200">
                    {orgDetails.slug}
                    <button
                      onClick={() => navigator.clipboard.writeText(orgDetails.slug)}
                      className="text-surface-500 hover:text-surface-300"
                    >
                      <Copy className="h-3.5 w-3.5" />
                    </button>
                  </dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt className="text-sm text-surface-400">Industry</dt>
                  <dd className="text-sm text-surface-200">
                    {orgDetails.industry || "—"}
                  </dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt className="text-sm text-surface-400">Contact Email</dt>
                  <dd className="text-sm text-surface-200">
                    {orgDetails.contact_email || "—"}
                  </dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt className="text-sm text-surface-400">Contact Phone</dt>
                  <dd className="text-sm text-surface-200">
                    {orgDetails.contact_phone || "—"}
                  </dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt className="text-sm text-surface-400">Contact Person</dt>
                  <dd className="text-sm text-surface-200">
                    {orgDetails.contact_person || "—"}
                  </dd>
                </div>
                <div className="flex items-start justify-between">
                  <dt className="text-sm text-surface-400">Address</dt>
                  <dd className="text-sm text-surface-200 text-right max-w-xs whitespace-pre-line">
                    {orgDetails.address || "—"}
                  </dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt className="text-sm text-surface-400">Status</dt>
                  <dd>
                    <StatusBadge status={orgDetails.status} />
                  </dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt className="text-sm text-surface-400">Sites</dt>
                  <dd className="text-sm text-surface-200">
                    {(currentOrg?.virtual_sites || []).length}
                  </dd>
                </div>
              </dl>
            ) : (
              <p className="text-surface-500 text-sm">
                Select an organization to view details
              </p>
            )}
          </Card>

          <Card>
            <CardHeader>
              <div className="flex items-center justify-between w-full">
                <CardTitle>Your Profile</CardTitle>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={openEditProfile}
                  disabled={!user}
                >
                  <Edit className="h-4 w-4" />
                  Edit
                </Button>
              </div>
            </CardHeader>
            {user ? (
              <dl className="space-y-4">
                <div className="flex items-center justify-between">
                  <dt className="text-sm text-surface-400">Name</dt>
                  <dd className="text-sm font-medium text-surface-200">
                    {user.full_name}
                  </dd>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <dt className="text-sm text-surface-400">Email</dt>
                  <dd className="flex items-center gap-2 text-sm text-surface-200">
                    <span className="truncate">{user.email}</span>
                    {user.is_email_verified ? (
                      <span title="Verified" className="inline-flex items-center gap-1 text-xs text-emerald-300">
                        <CheckCircle2 className="h-3.5 w-3.5" /> verified
                      </span>
                    ) : (
                      <>
                        <span className="inline-flex items-center gap-1 text-xs text-amber-300">
                          <AlertCircle className="h-3.5 w-3.5" /> pending
                        </span>
                        <Button variant="secondary" size="sm" onClick={() => openVerify("email")}>
                          <Send className="h-3.5 w-3.5" /> Verify
                        </Button>
                      </>
                    )}
                  </dd>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <dt className="text-sm text-surface-400">Phone</dt>
                  <dd className="flex items-center gap-2 text-sm text-surface-200">
                    {user.phone ? (
                      <>
                        <Phone className="h-3.5 w-3.5 text-surface-400" />
                        <span>{user.phone}</span>
                        {user.is_phone_verified ? (
                          <span title="Verified" className="inline-flex items-center gap-1 text-xs text-emerald-300">
                            <CheckCircle2 className="h-3.5 w-3.5" /> verified
                          </span>
                        ) : (
                          <>
                            <span className="inline-flex items-center gap-1 text-xs text-amber-300">
                              <AlertCircle className="h-3.5 w-3.5" /> pending
                            </span>
                            <Button variant="secondary" size="sm" onClick={() => openVerify("phone")}>
                              <Send className="h-3.5 w-3.5" /> Verify
                            </Button>
                          </>
                        )}
                      </>
                    ) : (
                      <span className="text-surface-500">— not set —</span>
                    )}
                  </dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt className="text-sm text-surface-400">Member since</dt>
                  <dd className="text-sm text-surface-200">
                    {formatDate(user.created_at)}
                  </dd>
                </div>
                {user.is_superuser && (
                  <div className="flex items-center justify-between">
                    <dt className="text-sm text-surface-400">Role</dt>
                    <dd>
                      <Badge variant="brand">Super Admin</Badge>
                    </dd>
                  </div>
                )}
              </dl>
            ) : (
              <p className="text-surface-500 text-sm">Loading profile...</p>
            )}
          </Card>

          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Palette className="h-5 w-5" />
                Appearance
              </CardTitle>
            </CardHeader>
            <p className="text-sm text-surface-400 mb-4">
              Choose your preferred theme. Settings are saved to your account
              and applied on every device you sign in from.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {([
                { mode: "dark" as const, label: "Dark", desc: "The original midnight glass.", icon: Moon },
                { mode: "light" as const, label: "Light Cream", desc: "Warm cream tones with frosted glass.", icon: Sun },
                { mode: "system" as const, label: "Match System", desc: "Follow your OS preference automatically.", icon: Monitor },
              ]).map(({ mode, label, desc, icon: Icon }) => {
                const active = themeMode === mode;
                const busy = themeSaving === mode;
                // The active state is a brand-colored ring around the standard
                // glass tile — keeping the text at its theme-native color so
                // it stays readable in both light and dark mode (an opaque
                // brand fill would force white text and become illegible on
                // cream).
                return (
                  <button
                    key={mode}
                    onClick={() => handleThemeChange(mode)}
                    disabled={busy}
                    className={`group liquid-glass-subtle liquid-glass-hover relative flex flex-col items-start gap-2 rounded-xl p-4 text-left ${
                      active ? "ring-2 ring-brand-500/60 ring-offset-2 ring-offset-transparent" : ""
                    } ${busy ? "opacity-60 pointer-events-none" : ""}`}
                  >
                    <div className="flex w-full items-center gap-2">
                      <Icon className={`h-4 w-4 ${active ? "text-brand-400" : "text-surface-400"}`} />
                      <span className="text-sm font-semibold text-surface-100">
                        {label}
                      </span>
                      {active && (
                        <CheckCircle2 className="h-4 w-4 text-brand-400 ml-auto" />
                      )}
                    </div>
                    <span className="text-xs text-surface-400">{desc}</span>
                  </button>
                );
              })}
            </div>
          </Card>

          {currentOrg && (currentOrg.virtual_sites || []).length > 0 && (
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle>Virtual Sites</CardTitle>
              </CardHeader>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {(currentOrg.virtual_sites || []).map((site) => (
                  <div
                    key={site.id}
                    className="rounded-lg border border-surface-700/50 bg-surface-800/50 p-4"
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-medium text-surface-200">
                          {site.name}
                        </p>
                        <p className="text-xs text-surface-500">{site.code}</p>
                      </div>
                      <StatusBadge
                        status={site.is_active ? "active" : "inactive"}
                      />
                    </div>
                    {site.location && (
                      <p className="mt-2 text-xs text-surface-400">
                        {site.location}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>
      )}

      {/* Users Tab */}
      {activeTab === "users" && (
        <Card>
          <CardHeader>
            <CardTitle>Organization Users</CardTitle>
            <Button variant="secondary" size="icon" onClick={fetchUsers}>
              <RefreshCw className="h-4 w-4" />
            </Button>
          </CardHeader>
          {isLoading ? (
            <div className="flex justify-center py-12">
              <LoadingSpinner size="md" />
            </div>
          ) : users.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-surface-500">
              <Users className="h-10 w-10 mb-3 text-surface-600" />
              <p>No users found</p>
            </div>
          ) : (
            <div className="space-y-3">
              {users.map((u) => (
                <div
                  key={u.id}
                  className="flex items-center justify-between rounded-lg border border-surface-700/50 bg-surface-800/50 p-3"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-brand-500 to-purple-500 text-xs font-bold text-white">
                      {u.full_name
                        .split(" ")
                        .map((n) => n[0])
                        .join("")
                        .toUpperCase()
                        .slice(0, 2)}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-surface-200">
                        {u.full_name}
                      </p>
                      <p className="text-xs text-surface-500">{u.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {u.roles.map((r, idx) => (
                      <Badge key={idx} variant="brand">
                        {r.role}
                      </Badge>
                    ))}
                    <StatusBadge
                      status={u.is_active ? "active" : "inactive"}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {/* Roles Tab */}
      {activeTab === "roles" && (
        <Card>
          <CardHeader>
            <CardTitle>Role Assignments</CardTitle>
          </CardHeader>
          <div className="space-y-4">
            <p className="text-sm text-surface-400">
              Role hierarchy: Super Admin {">"} Org Admin {">"} Site Admin {">"}{" "}
              User {">"} Contractor
            </p>
            <div className="rounded-lg border border-surface-700/50 overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-surface-700/50 bg-surface-800/50">
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-surface-400">
                      Role
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-surface-400">
                      Scope
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-surface-400">
                      Description
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    {
                      role: "super_admin",
                      scope: "Platform",
                      desc: "Full platform access. Bypasses all RBAC.",
                    },
                    {
                      role: "org_admin",
                      scope: "Organization",
                      desc: "Full control of organization and all sites.",
                    },
                    {
                      role: "site_admin",
                      scope: "Virtual Site",
                      desc: "Admin access to assigned sites.",
                    },
                    {
                      role: "user",
                      scope: "Virtual Site",
                      desc: "Standard read/write on assigned sites.",
                    },
                    {
                      role: "contractor",
                      scope: "Virtual Site",
                      desc: "Limited external access on assigned sites.",
                    },
                  ].map((r) => (
                    <tr
                      key={r.role}
                      className="border-b border-surface-700/30"
                    >
                      <td className="px-4 py-3">
                        <Badge variant="brand">{r.role}</Badge>
                      </td>
                      <td className="px-4 py-3 text-sm text-surface-300">
                        {r.scope}
                      </td>
                      <td className="px-4 py-3 text-sm text-surface-400">
                        {r.desc}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </Card>
      )}

      {/* Invitations Tab */}
      {activeTab === "invitations" && (
        <Card>
          <CardHeader>
            <CardTitle>Invitations</CardTitle>
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                size="icon"
                onClick={fetchInvitations}
              >
                <RefreshCw className="h-4 w-4" />
              </Button>
              <Button onClick={() => setShowInvite(true)}>
                <Plus className="h-4 w-4" />
                Invite User
              </Button>
            </div>
          </CardHeader>
          {isLoading ? (
            <div className="flex justify-center py-12">
              <LoadingSpinner size="md" />
            </div>
          ) : invitations.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-surface-500">
              <Mail className="h-10 w-10 mb-3 text-surface-600" />
              <p>No invitations sent</p>
            </div>
          ) : (
            <div className="space-y-3">
              {invitations.map((inv) => {
                const invStatus = inv.accepted
                  ? "completed"
                  : new Date(inv.expires_at) < new Date()
                    ? "overdue"
                    : "pending";
                return (
                  <div
                    key={inv.id}
                    className="flex items-center justify-between rounded-lg border border-surface-700/50 bg-surface-800/50 p-3"
                  >
                    <div className="flex items-center gap-3">
                      <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-purple-500/15">
                        <Mail className="h-4 w-4 text-purple-400" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-surface-200">
                          {inv.email}
                        </p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <Badge variant="brand">{inv.role}</Badge>
                          <span className="text-xs text-surface-500">
                            Expires {formatDate(inv.expires_at)}
                          </span>
                        </div>
                      </div>
                    </div>
                    <StatusBadge status={invStatus} />
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      )}

      {/* SSO Tab */}
      {activeTab === "sso" && (
        <Card>
          <CardHeader>
            <CardTitle>SSO Configurations</CardTitle>
            <Button variant="secondary" size="icon" onClick={fetchSSO}>
              <RefreshCw className="h-4 w-4" />
            </Button>
          </CardHeader>
          {isLoading ? (
            <div className="flex justify-center py-12">
              <LoadingSpinner size="md" />
            </div>
          ) : ssoConfigs.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-surface-500">
              <Link2 className="h-10 w-10 mb-3 text-surface-600" />
              <p>No SSO providers configured</p>
              <p className="text-xs mt-1">
                SSO configuration is managed by administrators
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {ssoConfigs.map((config) => (
                <div
                  key={config.id}
                  className="flex items-center justify-between rounded-lg border border-surface-700/50 bg-surface-800/50 p-3"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-500/15">
                      <Link2 className="h-4 w-4 text-blue-400" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-surface-200">
                        {config.provider}
                      </p>
                      <p className="text-xs text-surface-500">
                        Client ID: {config.client_id.slice(0, 20)}...
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <StatusBadge
                      status={config.enabled ? "active" : "inactive"}
                    />
                    <Button variant="ghost" size="icon-sm">
                      <Trash2 className="h-4 w-4 text-red-400" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {/* Edit Organization Modal */}
      <Modal
        isOpen={showEditOrg}
        onClose={() => { setShowEditOrg(false); setOrgError(null); }}
        title="Edit Organization"
      >
        <div className="space-y-4">
          {orgError && (
            <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300 flex items-start gap-2">
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>{orgError}</span>
            </div>
          )}
          <Input
            label="Name"
            value={orgForm.name}
            onChange={(e) => setOrgForm({ ...orgForm, name: e.target.value })}
            required
          />
          <Input
            label="Industry"
            placeholder="e.g., Oil & Gas"
            value={orgForm.industry}
            onChange={(e) => setOrgForm({ ...orgForm, industry: e.target.value })}
          />
          <Input
            label="Contact Email"
            type="email"
            value={orgForm.contact_email}
            onChange={(e) => setOrgForm({ ...orgForm, contact_email: e.target.value })}
          />
          <Input
            label="Contact Phone"
            value={orgForm.contact_phone}
            onChange={(e) => setOrgForm({ ...orgForm, contact_phone: e.target.value })}
          />
          <Input
            label="Contact Person"
            value={orgForm.contact_person}
            onChange={(e) => setOrgForm({ ...orgForm, contact_person: e.target.value })}
          />
          <Input
            label="Address"
            value={orgForm.address}
            onChange={(e) => setOrgForm({ ...orgForm, address: e.target.value })}
          />
          <div>
            <label className="block text-sm font-medium text-surface-300 mb-1.5">Status</label>
            <select
              value={orgForm.status}
              onChange={(e) => setOrgForm({ ...orgForm, status: e.target.value })}
              className="w-full rounded-lg border border-surface-600 bg-surface-800/50 px-4 py-2.5 text-sm text-surface-100 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
            >
              <option value="active">active</option>
              <option value="inactive">inactive</option>
              <option value="suspended">suspended</option>
            </select>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="secondary" onClick={() => { setShowEditOrg(false); setOrgError(null); }}>
              Cancel
            </Button>
            <Button onClick={handleSaveOrg} isLoading={isSavingOrg}>
              Save Changes
            </Button>
          </div>
        </div>
      </Modal>

      {/* Edit Profile Modal */}
      <Modal
        isOpen={showEditProfile}
        onClose={() => { setShowEditProfile(false); setProfileError(null); }}
        title="Edit Your Profile"
      >
        <div className="space-y-4">
          {profileError && (
            <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300 flex items-start gap-2">
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>{profileError}</span>
            </div>
          )}
          <Input
            label="Full Name"
            value={profileForm.full_name}
            onChange={(e) => setProfileForm({ ...profileForm, full_name: e.target.value })}
            required
          />
          <Input
            label="Phone"
            placeholder="+1 555 123 4567"
            value={profileForm.phone}
            onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })}
          />
          <p className="text-xs text-surface-500">
            Email can't be changed here — contact support if you need to update it.
            Phone changes will need to be re-verified.
          </p>
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="secondary" onClick={() => { setShowEditProfile(false); setProfileError(null); }}>
              Cancel
            </Button>
            <Button onClick={handleSaveProfile} isLoading={isSavingProfile}>
              Save Changes
            </Button>
          </div>
        </div>
      </Modal>

      {/* Verify Modal */}
      <Modal
        isOpen={!!verifyKind}
        onClose={() => { setVerifyKind(null); setVerifyError(null); setVerifyInfo(null); }}
        title={verifyKind === "email" ? "Verify Email" : "Verify Phone"}
      >
        <div className="space-y-4">
          {verifyError && (
            <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300 flex items-start gap-2">
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>{verifyError}</span>
            </div>
          )}
          {verifyInfo && (
            <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-300 flex items-start gap-2">
              <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" />
              <span>{verifyInfo}</span>
            </div>
          )}
          <p className="text-sm text-surface-300">
            Enter the 6-digit code we sent to{" "}
            <span className="font-medium text-surface-100">
              {verifyKind === "email" ? user?.email : user?.phone}
            </span>
            .
          </p>
          <Input
            label="6-digit code"
            placeholder="000000"
            value={verifyCode}
            onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            maxLength={6}
          />
          <div className="flex items-center justify-between pt-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => verifyKind && sendOtp(verifyKind)}
              isLoading={isSendingOtp}
            >
              <RefreshCw className="h-3.5 w-3.5" /> Resend code
            </Button>
            <div className="flex gap-3">
              <Button
                variant="secondary"
                onClick={() => { setVerifyKind(null); setVerifyError(null); setVerifyInfo(null); }}
              >
                Cancel
              </Button>
              <Button onClick={handleVerify} isLoading={isVerifying} disabled={verifyCode.length !== 6}>
                Verify
              </Button>
            </div>
          </div>
        </div>
      </Modal>

      {/* Invite Modal */}
      <Modal
        isOpen={showInvite}
        onClose={() => {
          setShowInvite(false);
          setInviteError(null);
        }}
        title="Invite User"
      >
        <div className="space-y-4">
          {inviteError && (
            <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
              {inviteError}
            </div>
          )}
          <Input
            label="Email"
            type="email"
            placeholder="user@company.com"
            value={inviteForm.email}
            onChange={(e) =>
              setInviteForm({ ...inviteForm, email: e.target.value })
            }
            required
          />
          <div className="space-y-1.5">
            <label className="block text-sm font-medium text-surface-300">
              Role
            </label>
            <select
              value={inviteForm.role}
              onChange={(e) =>
                setInviteForm({ ...inviteForm, role: e.target.value })
              }
              className="w-full rounded-lg border border-surface-600 bg-surface-800/50 px-4 py-2.5 text-sm text-surface-100 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
            >
              <option value="user">User</option>
              <option value="site_admin">Site Admin</option>
              <option value="org_admin">Org Admin</option>
              <option value="contractor">Contractor</option>
            </select>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <Button
              variant="secondary"
              onClick={() => {
                setShowInvite(false);
                setInviteError(null);
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleInvite} isLoading={isInviting}>
              Send Invitation
            </Button>
          </div>
        </div>
      </Modal>
    </motion.div>
  );
}
